/* eslint-disable no-console */
import tokenCheck from '../helpers/guards/tokenCheck';
import { roleInMerchant } from '../helpers/guards/roleInMerchant';
import hasPermissionInOrganisation from '../helpers/guards/hasPermissionInOrganisation';
import getSubscriptionInfo from '../helpers/guards/getSubscriptionInfo';
import { getLastOrgId } from '../helpers/localStorage.helpers';
import store from '../store';

export default {
  showSplash(to, from, next) {
    if (!from.meta.skipOrgCheck) {
      store.dispatch('organisation/setOrganisationSplash', { message: 'Getting everything ready...' });
    }
    next();
  },
  async checkToken(to, from, next) {
    if (tokenCheck()) {
      // await needed here so we are sure the settings are loaded
      if (!store.getters['system/webApiRoot']) {
        if (window.todesktop) {
          await store.dispatch('system/fetchDesktopSettings', null, { root: true });
        } else {
          await store.dispatch('system/fetchSettings', null, { root: true });
        }
      }
      next();
    } else {
      store.commit('reset');
      next({ name: 'LoginPage' });
    }
  },
  checkTokenOpposite(to, from, next) {
    if (!tokenCheck()) {
      next();
    } else if (to.query.next) {
      // Since we logout from guard the App component will catch the user signed out info and will redirect back to login but the parameters will be lost
      // since App component doesn't see parameters of the child component which is why sessionStorage is used as a bridge.
      sessionStorage.setItem('next', to.query.next);
      store.dispatch('user/logout');
    } else {
      let query = {};
      if (to.params.email && to.params.inviteToken) {
        query.invited = to.params.email;
      }
      query = { ...to.query, ...query };
      next({ name: 'logged-in-landing-page', query, params: to.params });
    }
  },

  /**/
  redirectToOrg(to, from, next) {
    if (!to.params.organisationId || !store.getters['organisation/organisationList'].find(org => org.id === to.params.organisationId)) {
      // Set loading layout
      if (!from.meta.skipOrgCheck) {
        store.dispatch('organisation/setOrganisationSplash', { message: 'Getting everything ready...' });
      }
      const errors = [];
      const loadingData = [
        {
          name: 'organisation/fetchOrganisations',
          payload: null,
          message: 'Getting everything ready...',
          callback: (response) => {
            if (!response.data.length) {
              errors.push('No organisations available.');
            }
          },
        },
        {
          name: 'user/fetchUser',
          payload: null,
          message: 'Getting everything ready...', // TODO make this message optional. This should be default message.
          callback: (response) => {
            if (!response.data.length) {
              errors.push('No user data available.');
            }
          },
        },
      ];

      const requests = [];
      loadingData.forEach((action) => {
        if (action.initial !== false) {
          store.dispatch('organisation/setSplashMessage', action.message);
          const request = store.dispatch(action.name, action.payload);
          request
            .then(action.callback)
            .catch(error => errors.push(error.message));

          requests.push(request);
        }
      });

      Promise.all(requests).then(() => {
        store.dispatch('organisation/splashProgressIncrease');
        if (store.getters['organisation/organisationList'].length > 0) {
          const orgIdToRedirectTo = getLastOrgId();
          if (from.meta.skipOrgCheck) {
            next();
          } else {
            next({
              name: 'OrganisationWithId',
              params: {
                organisationId: orgIdToRedirectTo,
              },
            });
          }
        } else {
          next({ name: 'NoOrganisation' });
        }
      });
    }
  },

  checkAndSetOrg(to, from, next) {
    if (!to.params.organisationId || !store.getters['organisation/organisationList'].find(org => org.id === to.params.organisationId) || !store.getters['organisation/organisationList'].length) {
      // Set loading layout
      if (!from.meta.skipOrgCheck) {
        store.dispatch('organisation/setOrganisationSplash', { message: 'Getting everything ready...' });
      }
      const errors = [];
      const loadingData = [
        // This is not needed since we are already fetching organisations on load, when we fix/rewrite the app then enable this
        // {
        //   name: 'organisation/fetchOrganisations',
        //   payload: null,
        //   message: 'Getting everything ready...',
        //   callback: (response) => {
        //     if (!response.data.length) {
        //       errors.push('No organisations available.');
        //     }
        //   },
        // },
      ];

      const requests = [];
      loadingData.forEach((action) => {
        if (action.initial !== false) {
          store.dispatch('organisation/setSplashMessage', action.message);
          const request = store.dispatch(action.name, action.payload);
          request
            .then(action.callback)
            .catch(error => errors.push(error.message));

          requests.push(request);
        }
      });

      Promise.all(requests).then(() => {
        store.dispatch('organisation/splashProgressIncrease');
        if (store.getters['organisation/organisationList'].length > 0) {
          const orgIdToRedirectTo = getLastOrgId();
          next({ name: 'FlowTestMain', params: { organisationId: orgIdToRedirectTo } }).catch(() => { });
        } else {
          next({ name: 'NoOrganisation' });
        }
      });
    } else {
      next();
    }
  },

  checkOrgId(to, from, next) {
    if (to.params.clearConversationCache) { // TODO rename to clearCache on rewrite
      store.dispatch('chat/clearConversationCache');
      store.dispatch('contact/clearContactCache');
    }

    if (store.getters['organisation/organisationList'].find(organisation => organisation.id === to.params.organisationId)) {
      store.dispatch('organisation/getOrgInfo', to.params.organisationId);
      next();
    } else {
      next({ name: 'Organisation' });
    }
  },

  isMerchantSubscribed(to, from, next) {
    const subscription = getSubscriptionInfo(to.params.merchant_id);

    if (subscription && subscription.active) {
      next();
    } else if (subscription && subscription.plan === 'no plan') {
      next({ name: 'NoPlanSubscription', params: { merchant_id: to.params.merchant_id } });
    } else {
      next({ name: 'PlanSubscription', params: { merchant_id: to.params.merchant_id } });
    }
  },

  needsToSubscribe(to, from, next) {
    const subscription = getSubscriptionInfo(to.params.merchant_id);

    if (subscription && subscription.active && subscription.plan !== 'no plan') {
      next({ name: 'Merchant', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },

  // Roles
  isMerchantMember(to, from, next) {
    const roleInTeam = roleInMerchant(to.params.merchant_id); // TODO role in merchant changed - refactor this

    if (!roleInTeam) {
      next({ name: 'BusinessList' });
    } else {
      next();
    }
  },
  isMerchantOwner(to, from, next) {
    const roleInTeam = roleInMerchant(to.params.merchant_id); // TODO role in merchant changed - refactor this

    if (!roleInTeam) {
      next({ name: 'BusinessList' });
    } else if (roleInTeam !== 'owner') {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },

  // Permissions
  hasManageSettingsPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('manage_settings');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
  hasManageAutomationPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('manage_automation');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
  hasManageContactsPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('manage_contacts');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
  hasManageInvoicesPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('manage_invoices');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
  hasViewInvoicesPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('view_invoices');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
  hasViewAnalyticsPermission(to, from, next) {
    const hasPermission = hasPermissionInOrganisation('view_analytics');

    if (!hasPermission) {
      next({ name: 'PageForbidden', params: { merchant_id: to.params.merchant_id } });
    } else {
      next();
    }
  },
};
